import React from "react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useState, useEffect } from "react"
import axiosInstance from "../../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../../constants/config"
import { ChevronUp, ChevronDown, ArrowUpDown, Plus, Edit, Trash2, Users, Car, UserCheck, AlertCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog"
import { toast } from "react-hot-toast"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import Pagination from "../../Common/Pagination"

const DriverManagement = () => {
  const [drivers, setDrivers] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [filterRole, setFilterRole] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [stats, setStats] = useState({
    totalDrivers: 0,
    totalConductors: 0,
    activeDrivers: 0,
    activeConductors: 0
  });
  
  const [newDriver, setNewDriver] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    contact: "",
    address: {
      street: "",
      city: "",
      state: "",
      pincode: "",
      country: "India"
    },
    licenseNumber: "",
    licenseExpiry: "",
    birthday: "",
    experience: "",
    role: "driver"
  });

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchDrivers();
    fetchStats();
  }, [filterRole, searchTerm]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      let url = API_ENDPOINTS.drivers;
      const params = new URLSearchParams();
      
      if (filterRole && filterRole !== "all") params.append("role", filterRole);
      if (searchTerm) params.append("search", searchTerm);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axiosInstance.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setDrivers(response.data.data.drivers);
    } catch (error) {
      console.error("Error fetching drivers:", error);
      toast.error("Failed to fetch drivers");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.driversStats, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // Validation function
  const validateForm = () => {
    const errors = {};
    
    if (!newDriver.firstName?.trim()) {
      errors.firstName = "First name is required";
    }
    
    if (!newDriver.contact?.trim()) {
      errors.contact = "Contact number is required";
    } else if (!/^[0-9]{10}$/.test(newDriver.contact.replace(/\s/g, ""))) {
      errors.contact = "Contact number must be exactly 10 digits";
    }
    
    if (newDriver.birthday) {
      const birthDate = new Date(newDriver.birthday);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 18 || age > 70) {
        errors.birthday = "Age must be between 18 and 70 years";
      }
    }
    
    if (newDriver.role === "driver") {
      if (newDriver.licenseExpiry) {
        const expiryDate = new Date(newDriver.licenseExpiry);
        const today = new Date();
        if (expiryDate <= today) {
          errors.licenseExpiry = "License expiry must be in the future";
        }
      }
    }
    
    if (newDriver.experience !== undefined && newDriver.experience !== "") {
      const exp = parseFloat(newDriver.experience);
      if (isNaN(exp) || exp < 0 || exp > 50) {
        errors.experience = "Experience must be between 0 and 50 years";
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }
    
    try {
      // Prepare data for API - no need to generate name field
      const driverData = {
        ...newDriver,
        experience: newDriver.experience ? parseFloat(newDriver.experience) : 0
      };
      
      if (selectedDriver) {
        // Update existing driver
        await axiosInstance.put(
          API_ENDPOINTS.driverById(selectedDriver._id),
          driverData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success("Driver updated successfully");
      } else {
        // Create new driver
        await axiosInstance.post(
          API_ENDPOINTS.drivers,
          driverData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        toast.success("Driver created successfully");
      }
      
      fetchDrivers();
      fetchStats();
      setIsModalOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving driver:", error);
      
      // Handle specific backend validation errors
      if (error.response?.data?.message) {
        const errorMessage = error.response.data.message;
        
        // Show specific error messages for different validation failures
        if (errorMessage.includes("License number already exists")) {
          toast.error("❌ License number already exists for another driver");
        } else if (errorMessage.includes("Contact number must be exactly 10 digits")) {
          toast.error("❌ Contact number must be exactly 10 digits");
        } else if (errorMessage.includes("Age must be between 18 and 70 years")) {
          toast.error("❌ Age must be between 18 and 70 years");
        } else if (errorMessage.includes("License expiry must be in the future")) {
          toast.error("❌ License expiry must be in the future");
        } else if (errorMessage.includes("Experience must be between 0 and 50 years")) {
          toast.error("❌ Experience must be between 0 and 50 years");
        } else {
          // Show generic error message
          toast.error(`❌ ${errorMessage}`);
        }
      } else {
        // Fallback error message
        toast.error("Failed to save driver. Please try again.");
      }
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(
        API_ENDPOINTS.driverById(selectedDriver._id),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      toast.success("Driver deleted successfully");
      fetchDrivers();
      fetchStats();
      setIsDeleteDialogOpen(false);
      setSelectedDriver(null);
    } catch (error) {
      console.error("Error deleting driver:", error);
      toast.error("Failed to delete driver");
    }
  };

  const resetForm = () => {
    setNewDriver({
      firstName: "",
      middleName: "",
      lastName: "",
      contact: "",
      address: {
        street: "",
        city: "",
        state: "",
        pincode: "",
        country: "India"
      },
      licenseNumber: "",
      licenseExpiry: "",
      birthday: "",
      experience: "",
      role: "driver"
    });
    setSelectedDriver(null);
    setFormErrors({});
  };

  const openEditModal = (driver) => {
    setSelectedDriver(driver);
    
    // Use firstName, middleName, lastName if available, otherwise split name
    const firstName = driver.firstName || driver.name?.split(' ')[0] || '';
    const lastName = driver.lastName || (driver.name ? driver.name.split(' ').pop() : '') || '';
    const middleName = driver.middleName || (driver.name && driver.name.split(' ').length > 2 ? driver.name.split(' ').slice(1, -1).join(' ') : '');
    
    setNewDriver({
      firstName,
      middleName,
      lastName,
      contact: driver.contact,
      address: driver.address || {
        street: "",
        city: "",
        state: "",
        pincode: "",
        country: "India"
      },
      licenseNumber: driver.licenseNumber || "",
      licenseExpiry: driver.licenseExpiry ? new Date(driver.licenseExpiry).toISOString().split('T')[0] : "",
      birthday: driver.birthday ? new Date(driver.birthday).toISOString().split('T')[0] : "",
      experience: driver.experience?.toString() || "",
      role: driver.role
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    
    setSortConfig({ key, direction });
  };

  const sortedDrivers = React.useMemo(() => {
    const sortableDrivers = [...drivers];
    if (sortConfig.key && sortConfig.direction) {
      sortableDrivers.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableDrivers;
  }, [drivers, sortConfig]);

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <ArrowUpDown className="ml-1 h-4 w-4 opacity-50" />;
    }
    
    return sortConfig.direction === 'ascending' ? 
      <ChevronUp className="ml-1 h-4 w-4 text-blue-600" /> : 
      <ChevronDown className="ml-1 h-4 w-4 text-blue-600" />;
  };

  const getHeaderProps = (columnName) => {
    return {
      onClick: () => requestSort(columnName),
      className: "cursor-pointer select-none hover:bg-gray-50 transition-colors"
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Drivers</CardTitle>
            <Car className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalDrivers}</div>
            <p className="text-xs text-muted-foreground">
              {stats.activeDrivers} active
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Conductors</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalConductors}</div>
            <p className="text-xs text-muted-foreground">
              {stats.activeConductors} active
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Staff</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalDrivers + stats.totalConductors}</div>
            <p className="text-xs text-muted-foreground">
              {stats.activeDrivers + stats.activeConductors} active
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Search Results</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{drivers.length}</div>
            <p className="text-xs text-muted-foreground">
              Showing filtered results
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Header with filters and create button */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex gap-2">
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="driver">Drivers</SelectItem>
                <SelectItem value="conductor">Conductors</SelectItem>
              </SelectContent>
            </Select>
            
            <Input
              placeholder="Search drivers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-48"
            />
          </div>
        </div>
        
        <Button onClick={openCreateModal} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" />
          Add Driver/Conductor
        </Button>
      </div>

      {/* Drivers Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead {...getHeaderProps('firstName')}>
                <div className="flex items-center">
                  Name {getSortIcon('firstName')}
                </div>
              </TableHead>
              <TableHead {...getHeaderProps('role')}>
                <div className="flex items-center">
                  Role {getSortIcon('role')}
                </div>
              </TableHead>
              <TableHead {...getHeaderProps('contact')}>
                <div className="flex items-center">
                  Contact {getSortIcon('contact')}
                </div>
              </TableHead>
              <TableHead>Experience</TableHead>
              <TableHead>License Info</TableHead>
              <TableHead {...getHeaderProps('status')}>
                <div className="flex items-center">
                  Status {getSortIcon('status')}
                </div>
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2">Loading drivers...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : sortedDrivers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  {searchTerm || filterRole !== "all" ? "No drivers found matching your criteria" : "No drivers added yet"}
                </TableCell>
              </TableRow>
            ) : (
              sortedDrivers.slice((page - 1) * itemsPerPage, page * itemsPerPage).map((driver) => (
                <TableRow key={driver._id}>
                  <TableCell className="font-medium">
                  {`${driver.firstName} ${driver.middleName ? driver.middleName + ' ' : ''}${driver.lastName}`.trim()}
                </TableCell>
                  <TableCell className="capitalize">{driver.role}</TableCell>
                  <TableCell>{driver.contact}</TableCell>
                  <TableCell>
                    {driver.experience ? `${driver.experience} years` : "N/A"}
                  </TableCell>
                  <TableCell>
                    {driver.role === "driver" ? (
                      <div className="text-sm">
                        <div>{driver.licenseNumber || "N/A"}</div>
                        <div className="text-gray-500">
                          {driver.licenseExpiry ? formatDate(driver.licenseExpiry) : "N/A"}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      driver.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {driver.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(driver)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setSelectedDriver(driver);
                          setIsDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={sortedDrivers.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setPage}
        onItemsPerPageChange={(newLimit) => { setItemsPerPage(newLimit); setPage(1); }}
        itemLabel="drivers"
      />

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedDriver ? "Edit Driver/Conductor" : "Add New Driver/Conductor"}
            </DialogTitle>
          </DialogHeader>
          
          {/* Show validation errors */}
          {Object.keys(formErrors).length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Please fix the following errors:
                <ul className="mt-2 list-disc list-inside">
                  {Object.values(formErrors).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={newDriver.firstName}
                  onChange={(e) =>
                    setNewDriver({ ...newDriver, firstName: e.target.value })
                  }
                  className={formErrors.firstName ? "border-red-500" : ""}
                  required
                />
                {formErrors.firstName && (
                  <p className="text-sm text-red-500">{formErrors.firstName}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Middle Name</Label>
                <Input
                  value={newDriver.middleName}
                  onChange={(e) =>
                    setNewDriver({ ...newDriver, middleName: e.target.value })
                  }
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={newDriver.lastName}
                  onChange={(e) =>
                    setNewDriver({ ...newDriver, lastName: e.target.value })
                  }
                  className={formErrors.lastName ? "border-red-500" : ""}
                />
                {formErrors.lastName && (
                  <p className="text-sm text-red-500">{formErrors.lastName}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Number *</Label>
                <Input
                  value={newDriver.contact}
                  onChange={(e) =>
                    setNewDriver({ ...newDriver, contact: e.target.value })
                  }
                  className={formErrors.contact ? "border-red-500" : ""}
                  placeholder="10 digit number"
                  required
                />
                {formErrors.contact && (
                  <p className="text-sm text-red-500">{formErrors.contact}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Experience (Years)</Label>
                <Input
                  type="number"
                  min="0"
                  max="50"
                  step="0.5"
                  value={newDriver.experience}
                  onChange={(e) =>
                    setNewDriver({ ...newDriver, experience: e.target.value })
                  }
                  placeholder="0"
                  className={formErrors.experience ? "border-red-500" : ""}
                />
                {formErrors.experience && (
                  <p className="text-sm text-red-500">{formErrors.experience}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select
                  value={newDriver.role}
                  onValueChange={(value) =>
                    setNewDriver({ ...newDriver, role: value })
                  }
                  required
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="conductor">Conductor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Birthday</Label>
                <Input
                  type="date"
                  value={newDriver.birthday}
                  onChange={(e) =>
                    setNewDriver({ ...newDriver, birthday: e.target.value })
                  }
                  className={formErrors.birthday ? "border-red-500" : ""}
                />
                {formErrors.birthday && (
                  <p className="text-sm text-red-500">{formErrors.birthday}</p>
                )}
              </div>
            </div>

            {/* License fields - only for drivers */}
            {newDriver.role === "driver" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>License Number</Label>
                  <Input
                    value={newDriver.licenseNumber}
                    onChange={(e) =>
                      setNewDriver({ ...newDriver, licenseNumber: e.target.value })
                    }
                    className={formErrors.licenseNumber ? "border-red-500" : ""}
                  />
                  {formErrors.licenseNumber && (
                    <p className="text-sm text-red-500">{formErrors.licenseNumber}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>License Expiry</Label>
                  <Input
                    type="date"
                    value={newDriver.licenseExpiry}
                    onChange={(e) =>
                      setNewDriver({ ...newDriver, licenseExpiry: e.target.value })
                    }
                    className={formErrors.licenseExpiry ? "border-red-500" : ""}
                  />
                  {formErrors.licenseExpiry && (
                    <p className="text-sm text-red-500">{formErrors.licenseExpiry}</p>
                  )}
                </div>
              </div>
            )}

            {/* Address fields */}
            <div className="space-y-2">
              <Label>Address</Label>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Street"
                  value={newDriver.address.street}
                  onChange={(e) =>
                    setNewDriver({
                      ...newDriver,
                      address: { ...newDriver.address, street: e.target.value }
                    })
                  }
                />
                <Input
                  placeholder="City"
                  value={newDriver.address.city}
                  onChange={(e) =>
                    setNewDriver({
                      ...newDriver,
                      address: { ...newDriver.address, city: e.target.value }
                    })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="State"
                  value={newDriver.address.state}
                  onChange={(e) =>
                    setNewDriver({
                      ...newDriver,
                      address: { ...newDriver.address, state: e.target.value }
                    })
                  }
                />
                <Input
                  placeholder="Pincode"
                  value={newDriver.address.pincode}
                  onChange={(e) =>
                    setNewDriver({
                      ...newDriver,
                      address: { ...newDriver.address, pincode: e.target.value }
                    })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="submit">
                {selectedDriver ? "Update" : "Add"} Driver/Conductor
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Are you sure you want to delete this driver/conductor?
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DriverManagement;